import { SearchOutlined } from '@ant-design/icons'
import { Divider, Select, Space, Spin, Typography } from 'antd'
import { countries } from 'countries-list'
import * as PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { useUser } from '../../../context/userContext'
import useDebounce from '../../../shared/hooks/useDebounce'
import GET_GOOGLE_PLACES from '@dentolize/clinic-mobile/src/shared/store/queries/actions/getGooglePlaces'
import GET_GOOGLE_PLACE_DETAILS from '@dentolize/clinic-mobile/src/shared/store/queries/actions/getGooglePlaceDetails'
import { useLazyQuery } from '@apollo/client'

const AddressField = props => {
  const { name, setFields, country, initialValue, companyId, formName, debounceTime } = props

  const [searchTerm, setSearchTerm] = useState('')
  const [value, setValue] = useState(initialValue)
  const [predictions, setPredictions] = useState([])

  const { user } = useUser()
  const { t } = useTranslation()

  const debouncedSearchTerm = useDebounce(searchTerm, debounceTime || 1500)

  const countryName = countries[country] ? country : user?.company.country ?? 'EG'

  const [getPlaces, { loading: loadingPlaces }] = useLazyQuery(GET_GOOGLE_PLACES)
  const [getPlaceDetails, { loading: loadingPlace }] = useLazyQuery(GET_GOOGLE_PLACE_DETAILS)

  useEffect(() => {
    if (debouncedSearchTerm && debouncedSearchTerm !== initialValue) {
      getPlaces({ variables: { searchTerm: debouncedSearchTerm, country: countryName, companyId } })
        .then(res => {
          if (res?.data?.getGooglePlaces && JSON.parse(res.data.getGooglePlaces)) {
            setPredictions(JSON.parse(res.data.getGooglePlaces))
          }
        })
        .catch()
    }
  }, [debouncedSearchTerm])

  const handleSearch = value => {
    setSearchTerm(value)
  }

  const handleSelect = async (e, item) => {
    if (item && item.key === 'searchTerm' && item.value) {
      setValue(item.value)
      setFields([{ name, value: item.value }])
    } else {
      const place = item.value

      if (place) {
        try {
          const res = await getPlaceDetails({ variables: { searchTerm: item.value, companyId } })

          if (res.data.getGooglePlaceDetails && JSON.parse(res.data.getGooglePlaceDetails)) {
            const data = JSON.parse(res.data.getGooglePlaceDetails)
            if (data.Geometry?.Point?.length) {
              setFields([
                { name: 'latitude', value: data.Geometry.Point[1] },
                { name: 'longitude', value: data.Geometry.Point[0] },
                { name: 'state', value: data.SubRegion }
              ])
            }
          }
        } catch (e) {
          console.log(e)
        }
      }

      setValue(item?.label ?? '')
      setFields([{ name, value: item?.label ?? '' }])
    }
  }

  const handleClear = () => {
    setValue(null)
    setFields([{ name, value: null }])
  }

  const dropdownRender = menu => {
    if (loadingPlace || loadingPlaces || searchTerm !== debouncedSearchTerm) {
      return (
        <div className="center">
          <Space direction="vertical">
            <Spin />
            <Typography.Text type="warning">{t('app.searchingPlaces')}</Typography.Text>
          </Space>
        </div>
      )
    }
    return (
      <>
        {menu}
        {predictions.length || searchTerm ? (
          <div className="center">
            <Divider style={{ margin: '8px 0' }} />
            <Typography.Text type="warning" strong>
              {predictions.length ? t('app.selectAddress') : t('app.addressNotFound')}
            </Typography.Text>
          </div>
        ) : null}
      </>
    )
  }

  return (
    <Select
      filterOption={() => true}
      className="fixArrowSelection full-width"
      showSearch
      autoComplete="off"
      allowClear
      suffixIcon={<SearchOutlined />}
      showAction={['click', isMobile ? '' : 'focus']}
      onSearch={handleSearch}
      loading={loadingPlaces || loadingPlace || searchTerm !== debouncedSearchTerm}
      notFoundContent={
        <div className="center">
          <Typography.Text type="warning" strong>
            {t('app.searchAddress')}
          </Typography.Text>
        </div>
      }
      onSelect={handleSelect}
      onClear={handleClear}
      value={value}
      data-testid={`${formName || 'patientForm'}-addressField`}
      dropdownRender={dropdownRender}
      options={
        predictions?.length
          ? predictions
              .filter(item => item.PlaceId && item.Text)
              .map(item => ({
                label: item.Text,
                value: item.PlaceId
              }))
          : searchTerm
          ? [{ label: searchTerm, value: searchTerm, key: 'searchTerm' }]
          : []
      }
    />
  )
}

AddressField.propTypes = {
  setFields: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  country: PropTypes.string
}

export default AddressField
